/**
 * Coordinate conversion utilities for the template editor.
 * JustSign uses normalized 0-1 coordinates (percentage of page dimensions).
 */

/**
 * Convert pixel coordinates to normalized (0-1) coordinates
 * @param {Object} pixelCoords - { x, y, w, h } in pixels
 * @param {number} pageWidth - Page width in pixels
 * @param {number} pageHeight - Page height in pixels
 * @returns {Object} - Normalized coordinates { x, y, w, h }
 */
export const pixelToNormalized = (pixelCoords, pageWidth, pageHeight) => {
  if (!pageWidth || !pageHeight) {
    console.warn('Invalid page dimensions for coordinate conversion');
    return pixelCoords;
  }

  return {
    x: pixelCoords.x / pageWidth,
    y: pixelCoords.y / pageHeight,
    w: pixelCoords.w !== undefined ? pixelCoords.w / pageWidth : undefined,
    h: pixelCoords.h !== undefined ? pixelCoords.h / pageHeight : undefined,
  };
};

/**
 * Convert normalized coordinates to pixels for rendering
 * @param {Object} normalizedCoords - { x, y, w, h } as 0-1 values
 * @param {number} pageWidth - Page width in pixels
 * @param {number} pageHeight - Page height in pixels
 * @returns {Object} - Pixel coordinates { x, y, w, h }
 */
export const normalizedToPixel = (normalizedCoords, pageWidth, pageHeight) => {
  if (!pageWidth || !pageHeight) {
    console.warn('Invalid page dimensions for coordinate conversion');
    return normalizedCoords;
  }

  return {
    x: normalizedCoords.x * pageWidth,
    y: normalizedCoords.y * pageHeight,
    w: normalizedCoords.w !== undefined ? normalizedCoords.w * pageWidth : undefined,
    h: normalizedCoords.h !== undefined ? normalizedCoords.h * pageHeight : undefined,
  };
};

/**
 * Clamp coordinates to stay within page bounds (0-1 range)
 * @param {Object} coords - { x, y, w, h, ...rest } normalized coordinates
 * @returns {Object} - Clamped coordinates (preserves additional properties like page)
 */
export const clampToPage = (coords) => {
  const w = Math.min(coords.w, 1);
  const h = Math.min(coords.h, 1);

  return {
    ...coords, // Preserve additional properties like 'page'
    x: Math.max(0, Math.min(coords.x, 1 - w)),
    y: Math.max(0, Math.min(coords.y, 1 - h)),
    w: w,
    h: h,
  };
};

/**
 * Snap coordinates to a grid for better alignment
 * @param {Object} coords - { x, y, w, h } normalized coordinates
 * @param {number} gridSize - Grid size as normalized value (default 0.005 = 0.5%)
 * @returns {Object} - Snapped coordinates
 */
export const snapToGrid = (coords, gridSize = 0.005) => {
  return {
    x: Math.round(coords.x / gridSize) * gridSize,
    y: Math.round(coords.y / gridSize) * gridSize,
    w: coords.w !== undefined ? Math.round(coords.w / gridSize) * gridSize : undefined,
    h: coords.h !== undefined ? Math.round(coords.h / gridSize) * gridSize : undefined,
  };
};

/**
 * Enforce minimum field dimensions
 * @param {Object} coords - { x, y, w, h } normalized coordinates
 * @param {number} minWidth - Minimum width (normalized)
 * @param {number} minHeight - Minimum height (normalized)
 * @returns {Object} - Coordinates with enforced minimums
 */
export const enforceMinimumSize = (coords, minWidth, minHeight) => {
  return {
    ...coords,
    w: Math.max(coords.w, minWidth),
    h: Math.max(coords.h, minHeight),
  };
};

/**
 * Calculate the center point of a field
 * @param {Object} area - { x, y, w, h } normalized coordinates
 * @returns {Object} - { x, y } center point
 */
export const getFieldCenter = (area) => {
  return {
    x: area.x + area.w / 2,
    y: area.y + area.h / 2,
  };
};

/**
 * Check if a point is inside a field area
 * @param {Object} point - { x, y } normalized coordinates
 * @param {Object} area - { x, y, w, h } normalized coordinates
 * @returns {boolean}
 */
export const isPointInField = (point, area) => {
  return (
    point.x >= area.x &&
    point.x <= area.x + area.w &&
    point.y >= area.y &&
    point.y <= area.y + area.h
  );
};

/**
 * Get the resize handle at a given point (if any)
 * @param {Object} point - { x, y } normalized coordinates
 * @param {Object} area - { x, y, w, h } normalized coordinates
 * @param {number} handleSize - Size of resize handles (normalized)
 * @returns {string|null} - Handle identifier ('nw', 'ne', 'sw', 'se') or null
 */
export const getResizeHandle = (point, area, handleSize = 0.015) => {
  const handles = {
    nw: { x: area.x, y: area.y },
    ne: { x: area.x + area.w, y: area.y },
    sw: { x: area.x, y: area.y + area.h },
    se: { x: area.x + area.w, y: area.y + area.h },
  };

  for (const [handle, pos] of Object.entries(handles)) {
    const dx = Math.abs(point.x - pos.x);
    const dy = Math.abs(point.y - pos.y);
    if (dx <= handleSize && dy <= handleSize) {
      return handle;
    }
  }

  return null;
};

/**
 * Apply a resize operation to field coordinates
 * @param {Object} area - Original { x, y, w, h } normalized coordinates
 * @param {string} handle - Resize handle ('nw', 'ne', 'sw', 'se')
 * @param {Object} delta - { dx, dy } change in normalized coordinates
 * @param {number} minWidth - Minimum width
 * @param {number} minHeight - Minimum height
 * @returns {Object} - New area coordinates
 */
export const applyResize = (area, handle, delta, minWidth, minHeight) => {
  let { x, y, w, h } = area;

  switch (handle) {
    case 'nw':
      x += delta.dx;
      y += delta.dy;
      w -= delta.dx;
      h -= delta.dy;
      break;
    case 'ne':
      y += delta.dy;
      w += delta.dx;
      h -= delta.dy;
      break;
    case 'sw':
      x += delta.dx;
      w -= delta.dx;
      h += delta.dy;
      break;
    case 'se':
      w += delta.dx;
      h += delta.dy;
      break;
    default:
      break;
  }

  // Enforce minimum size
  if (w < minWidth) {
    if (handle === 'nw' || handle === 'sw') {
      x = area.x + area.w - minWidth;
    }
    w = minWidth;
  }
  if (h < minHeight) {
    if (handle === 'nw' || handle === 'ne') {
      y = area.y + area.h - minHeight;
    }
    h = minHeight;
  }

  return clampToPage({ x, y, w, h });
};

/**
 * Convert mouse event to normalized page coordinates
 * @param {MouseEvent} event - Mouse event
 * @param {DOMRect} containerRect - Bounding rect of the PDF container
 * @param {number} pageWidth - Rendered page width in pixels
 * @param {number} pageHeight - Rendered page height in pixels
 * @returns {Object} - { x, y } normalized coordinates
 */
export const mouseEventToNormalized = (event, containerRect, pageWidth, pageHeight) => {
  const pixelX = event.clientX - containerRect.left;
  const pixelY = event.clientY - containerRect.top;

  return {
    x: pixelX / pageWidth,
    y: pixelY / pageHeight,
  };
};
